import { colors2 } from "oolib";
import { CONFIG as SIGNALS_CONFIG } from "@/_self_managed/SM_Dashboard/LensesV2/SignalsWidget/CONFIG";

/**
 * Signal-category constants — the four categories seeded on the tags tpl as
 * `main.category` (a RadioList), plus the palette every signal-coloured
 * surface shares.
 *
 * ONE palette, sourced from SignalsWidget/CONFIG.categoryStyle, so a signal
 * looks the same wherever it appears: the badge on a topic-cluster card
 * (SignalBadge), the accent on the finding side panel, and the filter chips
 * on the Signals + Topics tabs.
 *
 *   FILL   — the pill background (CONFIG.categoryStyle[cat].badgeBg): the
 *            base colour, flat.
 *   ACCENT — the base colour, for type, dots and outlines on white.
 */

export const SIGNAL_CATEGORY_FILL = Object.fromEntries(
  Object.entries(SIGNALS_CONFIG.categoryStyle).map(([cat, style]) => [cat, style.badgeBg])
);

// The four base colours (designer's palette, 2026-09-16): terracotta for
// dissatisfaction, ochre for behaviour, violet for needs, green for
// satisfaction. SignalsWidget/CONFIG uses the same four flat as pill fills.
export const SIGNAL_CATEGORY_ACCENT = {
  userDissatisfaction: "#D4553A",
  userSatisfaction: "#2FA075",
  userNeeds: "#8F55B3",
  userBehaviour: "#D9A02A",
};

// Colour of the small dot on a signal chip — the card badge's fill, so the
// chip and the badge read as the same signal.
export const SIGNAL_CATEGORY_COLOR = SIGNAL_CATEGORY_FILL;

// TagSelectV2 colour object: the base colour for the unselected outline +
// text, the same colour flat as the fill once selected, with CONFIG's
// on-fill text colour (white).
const catColor = (cat) => ({
  outline: SIGNAL_CATEGORY_ACCENT[cat],
  text: SIGNAL_CATEGORY_ACCENT[cat],
  fill: SIGNAL_CATEGORY_FILL[cat],
  textOnFill:
    (SIGNALS_CONFIG.categoryStyle[cat] || SIGNALS_CONFIG.defaultCategoryStyle).badgeColor,
});

// Selecting a category is equivalent to selecting every signal whose
// main.category.value matches.
export const SIGNAL_CATEGORY_OPTIONS = [
  { value: "userNeeds", display: "User Needs", color: catColor("userNeeds") },
  { value: "userBehaviour", display: "User Behaviour", color: catColor("userBehaviour") },
  { value: "userDissatisfaction", display: "User Dissatisfaction", color: catColor("userDissatisfaction") },
  { value: "userSatisfaction", display: "User Satisfaction", color: catColor("userSatisfaction") },
];

// Category value -> display order (matches the category chips). Signals
// without a category sort last.
export const CATEGORY_ORDER = Object.fromEntries(
  SIGNAL_CATEGORY_OPTIONS.map((o, i) => [o.value, i])
);

// ─── Per-tenant categories ─────────────────────────────────────────────────
//
// The constants above are the PM defaults. A tenant can carry its own signal
// categories (whatever `main.category` its signal tags hold — the BE serves
// the list as platformConfigs.SIGNAL_CATEGORIES and on the topic-clustering
// tree as `signalCategories`). `buildSignalCategoryTheme` turns that list
// into everything a signal-coloured surface needs: the ordered option list,
// the order map, and per-category fill / accent / style. PM categories keep
// their designed colours; any other category is assigned a stable colour
// from the palette below by its position in the list, so a tenant's chips,
// card bars, sparklines and donut rings all agree.

// Fill = pastel pill background, accent = readable dark counterpart. Hues
// chosen to stay clear of the four PM colours (red, green, purple, yellow).
const AUTO_CATEGORY_PALETTE = [
  { fill: "#7FD6D6", accent: "#127C7C" }, // teal
  { fill: "#FFB27F", accent: "#B85C12" }, // orange
  { fill: "#9CC3FF", accent: "#2D63C7" }, // blue
  { fill: "#F5A3D0", accent: "#B0308A" }, // pink
  { fill: "#C9B79C", accent: "#7A5A2E" }, // tan
  { fill: "#B6D98A", accent: "#4F7A1D" }, // olive
  { fill: "#B3B8E8", accent: "#3F4A9E" }, // indigo
  { fill: "#C7CDD6", accent: "#4A5566" }, // slate
];

// PM semantics, for when the served list carries none (older BE, tests):
// polarity = good/bad news for the product; severityWeight = the
// category-level default the Priorities view uses when a signal has no
// weight of its own. Mirrors BASE_TAXONOMY.
const PM_CATEGORY_SEMANTICS = {
  userNeeds: { polarity: "neutral", severityWeight: 0.4 },
  userBehaviour: { polarity: "neutral", severityWeight: 0.3 },
  userDissatisfaction: { polarity: "negative", severityWeight: 0.7 },
  userSatisfaction: { polarity: "positive", severityWeight: 0.1 },
};

const withSemantics = (c) => ({
  ...c,
  polarity: c.polarity || PM_CATEGORY_SEMANTICS[c.value]?.polarity || "neutral",
  severityWeight:
    typeof c.severityWeight === "number" ? c.severityWeight : PM_CATEGORY_SEMANTICS[c.value]?.severityWeight ?? null,
});

const DEFAULT_CATEGORIES = SIGNAL_CATEGORY_OPTIONS.map(({ value, display }) => withSemantics({ value, display }));

// Trend-chip tone follows polarity: a rising positive category is good news,
// a rising negative one is bad news, neutral is informational.
const toneOf = (polarity) => (polarity === "positive" ? "good" : polarity === "negative" ? "bad" : "neutral");

/**
 * @param {Array<{value, display, polarity?, severityWeight?, colorAs?}>} [categories]
 *   — the tenant's categories in display order (the BE serves polarity +
 *   weight on each, and optionally the PM category whose colour to borrow).
 *   Empty / undefined → PM defaults.
 * @param {object} [opts.prioritiesCopy] — Priorities-view copy overrides
 *   served with the tree, or null for the PM wording.
 */
export const buildSignalCategoryTheme = (categories, { prioritiesCopy = null } = {}) => {
  const list = categories?.length ? categories.map(withSemantics) : DEFAULT_CATEGORIES;

  const styleByValue = {};
  let autoIndex = 0;
  for (const { value, polarity, colorAs } of list) {
    // A tenant category may borrow a PM category's designed colour
    // (`colorAs`, served by the BE) instead of taking an auto hue.
    const pmKey = colorAs || value;
    const pm = SIGNALS_CONFIG.categoryStyle[pmKey];
    if (pm) {
      styleByValue[value] = { fill: pm.badgeBg, accent: SIGNAL_CATEGORY_ACCENT[pmKey], textOnFill: pm.badgeColor, tone: toneOf(polarity) };
    } else {
      const auto = AUTO_CATEGORY_PALETTE[autoIndex++ % AUTO_CATEGORY_PALETTE.length];
      styleByValue[value] = { fill: auto.fill, accent: auto.accent, textOnFill: SIGNALS_CONFIG.defaultCategoryStyle.badgeColor, tone: toneOf(polarity) };
    }
  }
  const fallback = {
    fill: SIGNALS_CONFIG.defaultCategoryStyle.badgeBg,
    accent: colors2.grey60,
    textOnFill: SIGNALS_CONFIG.defaultCategoryStyle.badgeColor,
    tone: SIGNALS_CONFIG.defaultCategoryStyle.tone,
  };
  const styleOf = (value) => styleByValue[value] || fallback;

  return {
    list,
    order: Object.fromEntries(list.map((c, i) => [c.value, i])),
    options: list.map(({ value, display }) => {
      const s = styleOf(value);
      return { value, display, color: { outline: s.accent, text: s.accent, fill: s.fill, textOnFill: s.textOnFill } };
    }),
    displayOf: (value) => list.find((c) => c.value === value)?.display || value,
    fillOf: (value) => styleOf(value).fill,
    accentOf: (value) => styleOf(value).accent,
    styleOf,
    polarityOf: (value) => list.find((c) => c.value === value)?.polarity || "neutral",
    // Categories that count as good news — the Priorities view's "wins".
    positiveCategories: list.filter((c) => c.polarity === "positive"),
    prioritiesCopy,
  };
};
